/**
 * Competition ranking ("1224"): tied values share the best rank in the group,
 * and the next distinct value skips ahead by the size of that group.
 * `values` must already be sorted by the ranking metric (best first).
 */
export function assignCompetitionRanks(values: readonly number[]): number[] {
  const ranks: number[] = [];
  for (let index = 0; index < values.length; index += 1) {
    const previousRank = ranks[index - 1];
    const isTie = index > 0 && values[index] === values[index - 1];
    ranks.push(isTie && previousRank !== undefined ? previousRank : index + 1);
  }
  return ranks;
}

export type ScoreTiebreakInput = {
  /** Displayed points, or correct-pick count. Higher is better. */
  rankingValue: number;
  /** Absolute distance from the tiebreaker total. Lower is better. */
  tiebreakerDelta: number;
};

/**
 * Orders standings by the displayed score, then by tiebreaker closeness.
 * A better tiebreaker never outranks a higher score.
 */
export function compareScoreThenTiebreaker(a: ScoreTiebreakInput, b: ScoreTiebreakInput): number {
  if (a.rankingValue !== b.rankingValue) {
    return b.rankingValue - a.rankingValue;
  }
  if (a.tiebreakerDelta !== b.tiebreakerDelta) {
    return a.tiebreakerDelta - b.tiebreakerDelta;
  }
  return 0;
}

export type SeasonStandingMetric = "points" | "correct";

export type SeasonStandingRow = {
  userId: string;
  totalPoints: number;
  totalCorrectPicks: number;
  tiebreakerDelta?: number | null;
};

const finiteTiebreaker = (value: number | null | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;

export function seasonStandingValue(row: SeasonStandingRow, metric: SeasonStandingMetric): number {
  return metric === "points" ? row.totalPoints : row.totalCorrectPicks;
}

/**
 * Season standings for one displayed metric. Tied metric values share a
 * competition rank; the tiebreaker only orders rows inside that tie.
 */
export function rankSeasonStandings<T extends SeasonStandingRow>(
  entries: readonly T[],
  metric: SeasonStandingMetric,
): Array<T & { rank: number }> {
  const sorted = [...entries].sort((a, b) => {
    const compared = compareScoreThenTiebreaker(
      {
        rankingValue: seasonStandingValue(a, metric),
        tiebreakerDelta: finiteTiebreaker(a.tiebreakerDelta),
      },
      {
        rankingValue: seasonStandingValue(b, metric),
        tiebreakerDelta: finiteTiebreaker(b.tiebreakerDelta),
      },
    );
    if (compared !== 0) return compared;
    return a.userId.localeCompare(b.userId);
  });
  const ranks = assignCompetitionRanks(sorted.map((entry) => seasonStandingValue(entry, metric)));
  return sorted.map((entry, index) => ({ ...entry, rank: ranks[index] ?? index + 1 }));
}
